import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  Field,
  FieldContent,
  FieldGroup,
  FieldLabel,
  FieldTitle,
  Icon,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  Tooltip,
} from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useAsyncRetry } from 'react-use';
import { useSnapshot } from 'valtio';
import { getProject } from '~/api/projects2';
import authorizations, { Action } from '~/authorizations';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';
import { EmbeddedCivilDrawMap } from './EmbeddedCivilDrawMap';
import permittingDrawStore from './permittingDrawStore';
import {
  formValuesToPermitFormData,
  getBasePermittingDefaults,
  mergePermittingFormDefaults,
  type PermittingProjectSource,
  permitDraftToFormValues,
} from './permittingFormDefaults';
import { emptyContractor, MAX_CONTRACTORS, type PermittingFormValues } from './permittingFormTypes';
import {
  capturePermittingMapSnapshotAfterPaint,
  getPermittingEmbedNorthArrowHtml,
  getPermittingEmbedScaleSnapshotHtml,
  getPermittingMapView,
  resetPermittingEmbedViewport,
} from './permittingMapSnapshot';
import { capturePermitPdfGenerateRequestSubmitted } from './permittingPrintPosthog';
import { buildPermittingPrintLegendHtml, printPermittingRequest } from './permittingPrintPreview';

function SectionTitle({ children }: { children: ReactNode }) {
  return <FieldTitle className="text-base font-semibold text-primary md:col-span-2">{children}</FieldTitle>;
}

const CONTRACTOR_TYPES = [
  { value: 'atmos', label: 'Atmos' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'other', label: 'Other' },
];

export function PermittingRequestForm() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { currentOrganizationId, me } = useSnapshot(rootStore);
  const { loadedFormValues, formHydrated } = useSnapshot(permittingDrawStore) as typeof permittingDrawStore;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const canDraw = authorizations.can(Action.EditProjectDetails);

  const project = useAsyncRetry(async () => {
    if (projectId === 'sample-project') {
      if (!currentOrganizationId) return undefined;
      return await getProject<PermittingProjectSource>(projectId, currentOrganizationId);
    }
    return await getProject<PermittingProjectSource>(projectId);
  }, [projectId, currentOrganizationId]);

  const { control, handleSubmit, register, reset, watch, getValues } = useForm<PermittingFormValues>({
    defaultValues: getBasePermittingDefaults(),
  });

  const { fields: contractorFields, append, remove } = useFieldArray({ control, name: 'contractors' });

  // Hydrate the form once per project: restore the in-memory session on tab remount, otherwise apply the
  // saved draft (when CivilDraw is available) or the project defaults.
  useEffect(() => {
    if (formHydrated) {
      if (permittingDrawStore.sessionFormValues) reset(permittingDrawStore.sessionFormValues);
      return;
    }
    if (project.loading) return;
    // Wait for the draft to resolve while the map (which triggers loadDraft) is loading.
    if (canDraw && loadedFormValues === undefined) return;

    const hasSavedDraft = canDraw && !!loadedFormValues;
    const initial = hasSavedDraft
      ? permitDraftToFormValues(loadedFormValues, project.value ?? null)
      : mergePermittingFormDefaults(project.value ?? null);
    reset(initial);
    permittingDrawStore.sessionFormValues = initial;
    permittingDrawStore.formHydrated = true;

    if (hasSavedDraft) {
      Notify.info({ message: 'Loaded your saved permit draft.' });
    }
  }, [formHydrated, canDraw, project.loading, project.value, loadedFormValues, reset]);

  // Mirror live form edits into the store so they survive projectDetails tab switches.
  useEffect(() => {
    const sub = watch((values) => {
      permittingDrawStore.sessionFormValues = values as PermittingFormValues;
    });
    return () => sub.unsubscribe();
  }, [watch]);

  /** Upserts the permit draft (form + contractors + map view + drawings). No-op without CivilDraw access. */
  const persistDraft = async (values: PermittingFormValues) => {
    if (!canDraw) return null;
    return permittingDrawStore.saveDraft({
      formData: formValuesToPermitFormData(values),
      contractors: values.contractors.slice(0, MAX_CONTRACTORS),
      mapView: getPermittingMapView(),
    });
  };

  const handleSaveDraft = async () => {
    if (!canDraw || isSaving) return;
    setIsSaving(true);
    try {
      const values = getValues();
      const draft = await persistDraft(values);
      capturePosthogEvent(POSTHOG_EVENTS.permitDraftSaved, {
        projectId,
        organizationId: currentOrganizationId ?? null,
        permitId: draft?.id ?? null,
        contractorCount: values.contractors.length,
      });
    } catch (e) {
      console.error('Failed to save permit draft', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDraft = async () => {
    await permittingDrawStore.deleteDraft();
    const defaults = mergePermittingFormDefaults(project.value ?? null);
    reset(defaults);
    permittingDrawStore.sessionFormValues = defaults;
    resetPermittingEmbedViewport();
    capturePosthogEvent(POSTHOG_EVENTS.permitDraftDeleted, {
      projectId,
      organizationId: currentOrganizationId ?? null,
    });
    setDeleteConfirmOpen(false);
  };

  const onSubmit = async (data: PermittingFormValues) => {
    capturePermitPdfGenerateRequestSubmitted({
      routeProjectId: projectId,
      organizationId: currentOrganizationId,
      userId: me?.id ?? null,
      userEmail: me?.email ?? null,
      project: project.value,
      form: data,
    });

    // Clear map selection/handles before capture so the printed map matches a neutral view.
    permittingDrawStore.resetCurrentObjectId();
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
    const mapDataUrl = await capturePermittingMapSnapshotAfterPaint();
    const displayProjectId = (project.value?.projectId ?? '').trim() || projectId;
    const { html: legendHtml, blockCount: legendBlockCount } = buildPermittingPrintLegendHtml();
    const scaleHtml = getPermittingEmbedScaleSnapshotHtml();
    const northArrowHtml = getPermittingEmbedNorthArrowHtml();
    printPermittingRequest(data, displayProjectId, mapDataUrl, {
      legendHtml,
      legendBlockCount,
      scaleHtml,
      northArrowHtml,
    });

    // Generating the PDF also persists the draft to the DB (map snapshot is already captured above,
    // so the save's file re-hydration can't affect the printed image).
    try {
      await persistDraft(data);
    } catch (e) {
      console.error('Failed to save permit draft on generate', e);
    }
  };

  /** Map stays a sibling of `<form>` (not inside it): native buttons in the embed default to `type="submit"`. */
  const formId = 'permitting-request-form';

  return (
    <div className="flex w-full max-w-6xl flex-col pb-6 font-noto">
      <div className="rounded border border-grey-30 bg-white p-4 shadow-[0px_1px_2px_0px_rgba(29,30,32,0.2),0px_1px_3px_0px_rgba(29,30,32,0.15)]">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-grey-30 pb-4">
          <h2 className="text-base font-semibold text-primary">Permit PDF Generation</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="danger"
              size="standard"
              disabled={project.loading || isSaving}
              data-testid="permitting-delete-draft-button"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete Draft
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="standard"
              loading={isSaving}
              disabled={project.loading || isSaving || !canDraw}
              data-testid="permitting-save-draft-button"
              onClick={handleSaveDraft}
            >
              Save Draft
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              size="standard"
              disabled={project.loading}
              data-testid="permitting-generate-button"
            >
              <Icon icon="file" size={14} />
              Save and Generate Request
            </Button>
          </div>
        </div>

        <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
          <FieldGroup className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-2">
            <SectionTitle>Permitting project information</SectionTitle>

            <div className="col-span-1 grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3 md:gap-x-4">
              <Field className="min-w-0">
                <FieldLabel>Permit submission date</FieldLabel>
                <FieldContent>
                  <Controller
                    name="permitSubmissionDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Today"
                        className="w-full"
                      />
                    )}
                  />
                </FieldContent>
              </Field>

              <Field className="min-w-0">
                <FieldLabel>Work start date</FieldLabel>
                <FieldContent>
                  <Controller
                    name="workStartDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select date"
                        className="w-full"
                      />
                    )}
                  />
                </FieldContent>
              </Field>

              <Field className="min-w-0">
                <FieldLabel>Work end date</FieldLabel>
                <FieldContent>
                  <Controller
                    name="workEndDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select date"
                        className="w-full"
                      />
                    )}
                  />
                </FieldContent>
              </Field>
            </div>

            <div className="col-span-1 grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3 md:gap-x-4">
              <Field className="min-w-0">
                <FieldLabel htmlFor="permitSubmissionContactName">Permit submission contact name</FieldLabel>
                <FieldContent>
                  <Input
                    id="permitSubmissionContactName"
                    placeholder="Name or role"
                    autoComplete="name"
                    {...register('permitSubmissionContactName')}
                  />
                </FieldContent>
              </Field>

              <Field className="min-w-0">
                <FieldLabel htmlFor="permitSubmissionContactEmail">Permit submission contact email</FieldLabel>
                <FieldContent>
                  <Input
                    id="permitSubmissionContactEmail"
                    type="email"
                    placeholder="Email"
                    autoComplete="email"
                    {...register('permitSubmissionContactEmail')}
                  />
                </FieldContent>
              </Field>

              <Field className="min-w-0">
                <FieldLabel htmlFor="permitSubmissionContactPhone">Permit submission contact phone</FieldLabel>
                <FieldContent>
                  <Input
                    id="permitSubmissionContactPhone"
                    type="tel"
                    placeholder="Phone"
                    autoComplete="tel"
                    {...register('permitSubmissionContactPhone')}
                  />
                </FieldContent>
              </Field>
            </div>

            <SectionTitle>Location</SectionTitle>

            <div className="col-span-1 grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3 md:gap-x-4">
              <Field className="min-w-0">
                <div className="flex items-center gap-1">
                  <FieldLabel htmlFor="address">Address</FieldLabel>
                  <Tooltip text="Use the project site address or the specific permit location." asChild>
                    <button
                      type="button"
                      className="inline-flex text-grey-60 hover:text-grey-80"
                      aria-label="Address field information"
                    >
                      <Icon icon="infoCircled" size={14} />
                    </button>
                  </Tooltip>
                </div>
                <FieldContent>
                  <Input id="address" placeholder="123 Street" autoComplete="street-address" {...register('address')} />
                </FieldContent>
              </Field>

              <Field className="min-w-0">
                <FieldLabel htmlFor="city">City</FieldLabel>
                <FieldContent>
                  <Input id="city" placeholder="Waco" autoComplete="address-level2" {...register('city')} />
                </FieldContent>
              </Field>

              <Field className="min-w-0">
                <FieldLabel htmlFor="zip">Zip</FieldLabel>
                <FieldContent>
                  <Input id="zip" placeholder="12345" autoComplete="postal-code" {...register('zip')} />
                </FieldContent>
              </Field>
            </div>

            <SectionTitle>Atmos contact</SectionTitle>

            <div className="col-span-1 grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3 md:gap-x-4">
              <Field className="min-w-0">
                <FieldLabel htmlFor="atmosRep">Atmos rep</FieldLabel>
                <FieldContent>
                  <Input id="atmosRep" placeholder="First and last name" {...register('atmosRep')} />
                </FieldContent>
              </Field>

              <Field className="min-w-0">
                <FieldLabel htmlFor="atmosEmail">Email</FieldLabel>
                <FieldContent>
                  <Input
                    id="atmosEmail"
                    type="email"
                    placeholder="PM email"
                    autoComplete="email"
                    {...register('atmosEmail')}
                  />
                </FieldContent>
              </Field>

              <Field className="min-w-0">
                <FieldLabel htmlFor="atmosPhone">Phone</FieldLabel>
                <FieldContent>
                  <Input
                    id="atmosPhone"
                    type="tel"
                    placeholder="Phone"
                    autoComplete="tel"
                    {...register('atmosPhone')}
                  />
                </FieldContent>
              </Field>
            </div>

            <SectionTitle>Project info</SectionTitle>

            <div className="flex flex-col gap-4 md:col-span-2 md:grid md:grid-cols-3 md:gap-x-4 md:items-start">
              <div className="flex flex-col gap-4 min-w-0">
                <Field className="min-w-0">
                  <FieldLabel>Work type</FieldLabel>
                  <FieldContent>
                    <Controller
                      name="workTypeRadio"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-6"
                        >
                          {(
                            [
                              ['main', 'Main line'],
                              ['service', 'Service line'],
                              ['emergency', 'Emergency'],
                            ] as const
                          ).map(([value, label]) => (
                            <div key={value} className="flex items-center gap-2">
                              <RadioGroupItem
                                value={value}
                                id={`work-type-${value}`}
                                data-testid={`work-type-${value}`}
                              />
                              <Label
                                htmlFor={`work-type-${value}`}
                                className="cursor-pointer text-xs font-normal text-grey-80"
                              >
                                {label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </FieldContent>
                </Field>

                <Field className="min-w-0">
                  <FieldLabel htmlFor="projectNumber">Project Number</FieldLabel>
                  <FieldContent>
                    <Input
                      id="projectNumber"
                      placeholder="e.g. PRJ-1024"
                      data-testid="permitting-project-number"
                      {...register('projectNumber')}
                    />
                  </FieldContent>
                </Field>
              </div>

              <Field className="min-w-0 md:col-span-2">
                <FieldLabel htmlFor="workTypeText">Work Description</FieldLabel>
                <FieldContent>
                  <textarea
                    id="workTypeText"
                    rows={4}
                    placeholder="Description of work"
                    data-testid="permitting-work-description"
                    className="min-h-[96px] w-full min-w-0 resize-y rounded border border-grey-30 bg-white px-2 py-1.5 text-xs leading-[1.4] text-grey-80 outline-none placeholder:text-grey-50 focus-visible:border-focus"
                    {...register('workTypeText')}
                  />
                </FieldContent>
              </Field>
            </div>

            <div className="flex items-center justify-between md:col-span-2">
              <SectionTitle>Contractors</SectionTitle>
              {contractorFields.length < MAX_CONTRACTORS ? (
                <button
                  type="button"
                  onClick={() => append(emptyContractor())}
                  data-testid="permitting-add-contractor"
                  className="flex items-center gap-1 self-start text-sm font-medium text-primary hover:underline"
                >
                  <Icon icon="plus" size={14} />
                  Add contractor
                </button>
              ) : null}
            </div>

            <div className="flex flex-col gap-2 md:col-span-2">
              {/* Column labels (shown once; each contractor is a single compact row below). */}
              <div className="hidden gap-2 px-1 md:grid md:grid-cols-[7rem_1fr_1fr_1.3fr_0.9fr_1.75rem]">
                {['Type', 'Contractor', 'Construction rep', 'Email', 'Phone'].map((label) => (
                  <span key={label} className="text-xs font-medium text-grey-60">
                    {label}
                  </span>
                ))}
                <span aria-hidden="true" />
              </div>

              {contractorFields.map((cf, index) => (
                <div
                  key={cf.id}
                  className="grid grid-cols-1 gap-2 md:grid-cols-[7rem_1fr_1fr_1.3fr_0.9fr_1.75rem] md:items-center"
                  data-testid={`permitting-contractor-row-${index}`}
                >
                  <Controller
                    name={`contractors.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={CONTRACTOR_TYPES}
                        value={field.value}
                        onValueChange={(next) => next && field.onChange(next)}
                        onBlur={field.onBlur}
                        triggerClassName="w-full"
                        aria-label={`Contractor ${index + 1} type`}
                        data-testid={`permitting-contractor-${index}-type`}
                      />
                    )}
                  />
                  <Input
                    aria-label={`Contractor ${index + 1} name`}
                    placeholder="Company or contractor"
                    data-testid={`permitting-contractor-${index}-name`}
                    {...register(`contractors.${index}.name`)}
                  />
                  <Input
                    aria-label={`Contractor ${index + 1} construction rep`}
                    placeholder="Construction rep"
                    data-testid={`permitting-contractor-${index}-repName`}
                    {...register(`contractors.${index}.repName`)}
                  />
                  <Input
                    type="email"
                    autoComplete="email"
                    aria-label={`Contractor ${index + 1} email`}
                    placeholder="Email"
                    data-testid={`permitting-contractor-${index}-email`}
                    {...register(`contractors.${index}.email`)}
                  />
                  <Input
                    type="tel"
                    autoComplete="tel"
                    aria-label={`Contractor ${index + 1} phone`}
                    placeholder="Phone"
                    data-testid={`permitting-contractor-${index}-phone`}
                    {...register(`contractors.${index}.phone`)}
                  />
                  {contractorFields.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      aria-label={`Remove contractor ${index + 1}`}
                      data-testid={`permitting-remove-contractor-${index}`}
                      className="inline-flex justify-center text-grey-60 hover:text-red-50"
                    >
                      <Icon icon="trash" size={14} />
                    </button>
                  ) : (
                    <span aria-hidden="true" />
                  )}
                </div>
              ))}
            </div>
          </FieldGroup>
        </form>

        <div className="mt-6 flex flex-col gap-3 border-t border-grey-30 pt-6">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-primary">Permit area</h2>
              <p className="text-xs text-grey-80">Select map area to include in permit.</p>
            </div>
            <Controller
              name="mapOnSeparatePage"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="permitting-map-separate-page"
                    checked={field.value}
                    onCheckedChange={(value) => field.onChange(value === true)}
                    data-testid="permitting-map-separate-page"
                  />
                  <Label
                    htmlFor="permitting-map-separate-page"
                    className="cursor-pointer text-xs font-normal text-grey-80"
                  >
                    Map on separate page
                  </Label>
                </div>
              )}
            />
          </div>
          {canDraw ? (
            <EmbeddedCivilDrawMap />
          ) : (
            <div className="relative flex w-full aspect-[16/9] items-center justify-center rounded border border-grey-30 bg-grey-10 px-4 text-center text-sm text-grey-80">
              You do not have access to CivilDraw maps.
            </div>
          )}
        </div>
      </div>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent
          className="z-60 w-full max-w-md gap-[18px] p-4"
          overlayClassName="z-60"
          title="Delete draft?"
          data-testid="permitting-delete-draft-dialog"
          headerProps={{ className: 'p-0' }}
          titleProps={{ className: 'p-0 text-[18px] font-semibold text-primary' }}
        >
          <DialogDescription className="p-0 text-xs font-normal leading-[1.4] text-grey-80">
            This archives the saved permit draft, resets all fields to their defaults, and removes any drawings on the
            permit area map. This cannot be undone.
          </DialogDescription>
          <DialogFooter className="flex items-center justify-end gap-2 p-0">
            <Button
              type="button"
              variant="secondary"
              size="standard"
              onClick={() => setDeleteConfirmOpen(false)}
              data-testid="permitting-delete-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              size="standard"
              data-testid="permitting-delete-confirm-button"
              onClick={handleDeleteDraft}
            >
              Delete draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
